import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import Sidebar from '../components/Sidebar';
import ChatArea from '../components/ChatArea';
import MessageInput from '../components/MessageInput';

const Messenger = () => {
  const { currentUser, loading } = useAuth();
  const { orders, selectedOrder, setSelectedOrder, selectOrder, fetchMessages } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize immediately from window width so the correct layout renders on first paint.
  // This prevents iOS Safari seeing a desktop layout flash (w-96 Sidebar overflowing the viewport).
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!loading && !currentUser) {
      navigate('/login');
    }
  }, [currentUser, loading, navigate]);

  // Re-fetch messages on mount when navigating back to messenger with an order already selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedOrder?.id) {
      fetchMessages(selectedOrder.id);
    }
  }, []); // Intentionally only runs on mount

  // Deep-link: open a specific order from a push (?order=<id>) or from a list
  // navigation (state.selectOrder). Runs once the orders list is available.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetId = params.get('order') || location.state?.selectOrder;
    if (!targetId || !orders || orders.length === 0) return;
    if (selectedOrder?.id === targetId) return;
    const match = orders.find((o) => o.id === targetId || o._id === targetId);
    if (match) {
      selectOrder(match);
      // Clear so a refresh / back doesn't re-trigger selection
      navigate('/messenger', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, location.search, location.state]);

  // Handle back button on mobile
  const handleBackToList = () => {
    setSelectedOrder(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="h-screen flex flex-col overflow-hidden">
        {/* Show Sidebar (order list) when no order selected */}
        {/* flex flex-col min-h-0: fixes iOS Safari h-full collapse inside flex-1 */}
        {!selectedOrder && (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <Sidebar isMobile={true} />
          </div>
        )}

        {/* Show Chat when order is selected */}
        {selectedOrder && (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <ChatArea isMobile={true} onBack={handleBackToList} />
            <MessageInput />
          </div>
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <ChatArea />
        <MessageInput />
      </div>
    </div>
  );
};

export default Messenger;
